import { Vector2, Vector3 } from "three";

/**
 * used to create specific length arrays of objects. more useful for larger arrays
 * @param constructionArguments default values the object should  be constructed with
 * ```typescript
 * // make array of length 10 filled with Vector3's initialized to the Y axis
 * const tenYAxes = makeArrayOfObjects(10, Vector3, 0,1,0);
 * ```
 */
export const makeArrayOfObjects = <T>(
  length: number,
  type: TConstructor<T>,
  ...constructionArguments: any[]
): T[] => {
  return Array.from({ length: length }, () => {
    return new type(...constructionArguments);
  });
};

export type TConstructor<T = {}> = new (...constructorArguments: any[]) => T;
export type TGenericVoidFunction = (...functionArguments: any[]) => void;
export type TControlMode = "trackball" | "orbit" | "custom";
export type TProjectionMode = "orthographic" | "perspective";
export type TEventHandleFunction = (ndcX: number, ndcY: number) => void;

export interface ICameraBehavior {
  rotateSpeed: number;
  panSpeed: number;
  zoomSpeed: number;
  setDeltaNDC: (val: Vector2) => void;

  start: (
    ndcX: number,
    ndcY: number
    // button?: PointerEventButtonIDs,
    // ifcProject?: IfcProjectManager,
    // ifcRenderer?: IFCRenderer
  ) => void;
  rotate: TEventHandleFunction;
  setRotateCenter(ndcX: number, ndcY: number): Vector3;
  // pan: (ndcX: number, ndcY: number, ifcProject?: IfcProjectManager) => void;
  pan: (ndcX: number, ndcY: number) => void;
  zoom: (delta: number, ndcX?: number, ndcY?: number) => void;
  getZoomDirection: (ndcX: number, ndcY: number) => number;
  wheel: (ndcX: number, ndcY: number, wheelDirection: 1 | -1) => void;
  alternativeWheel: boolean;

  end: TGenericVoidFunction;
  update: TGenericVoidFunction;
  flatZoom: (delta: 1 | -1) => void;
  flatRotate: (delta: 1 | -1) => void;
  flatPan: TEventHandleFunction;
  addHelper?: () => void;
  setHelperVisibility?: (visible: boolean) => void;
}

export type TPageCoordEvent = { pageX: number; pageY: number };
export type TElementBoundingRectangle = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export const pageCoordToNormalizedDeviceCoord = (
  event: TPageCoordEvent,
  boundingClientRect: TElementBoundingRectangle,
  output: Vector2
): Vector2 => {
  pageCoordToNormalizedTargetCoord(event, boundingClientRect, output);

  // convert from normalized target to normalized device
  output.x = output.x * 2 - 1;
  output.y = -(output.y * 2 - 1);
  return output;
};

export const pageCoordToNormalizedTargetCoord = (
  event: TPageCoordEvent,
  boundingClientRect: TElementBoundingRectangle,
  output: Vector2
): Vector2 => {
  const scrollLeft = Math.max(
    window.pageXOffset,
    document.documentElement.scrollLeft,
    document.body.scrollLeft
  );
  const scrollTop = Math.max(
    window.pageYOffset,
    document.documentElement.scrollTop,
    document.body.scrollTop
  );
  return output.set(
    (event.pageX - boundingClientRect.left - scrollLeft) /
      boundingClientRect.width,
    (event.pageY - boundingClientRect.top - scrollTop) /
      boundingClientRect.height
  );
};
